import type { VaultKey } from '@curentia/shared';
import type { LocalStore } from './store';

/** A pharmacy's notice that it dispensed against one of this doctor's prescriptions. */
export interface DoctorNotice {
  /** The dispensing practice and its record ID, which together name the notice. */
  key: string;
  pharmacy: { id: string; name: string };
  /** The prescription's own identifier, as its document carries it. */
  prescriptionId: string;
  patientName: string;
  prescribedAt: string;
  dispense: { id: string; version: number; resource: Record<string, unknown> };
  receivedAt: string;
}

const ITEM = 'notices';
/** Most notices a device keeps. The oldest go first. */
export const MAX_NOTICES = 500;

/**
 * The dispensing notices on a doctor's device, encrypted in the vault like
 * everything else and newest first. They tell the prescribing doctor what
 * each pharmacy handed over; the pharmacy keeps the record itself.
 */
export class DoctorNotices {
  private cache: DoctorNotice[] | null = null;

  constructor(
    private readonly db: LocalStore,
    private readonly key: VaultKey,
  ) {}

  async list(): Promise<DoctorNotice[]> {
    this.cache ??= (await this.db.get<DoctorNotice[]>(this.key, ITEM)) ?? [];
    return this.cache;
  }

  async forPrescription(prescriptionId: string): Promise<DoctorNotice[]> {
    return (await this.list()).filter((n) => n.prescriptionId === prescriptionId);
  }

  /** Keeps a notice. Returns false when this version or a newer one is already kept. */
  async take(notice: DoctorNotice): Promise<boolean> {
    const current = await this.list();
    const known = current.find((n) => n.key === notice.key);
    if (known && known.dispense.version >= notice.dispense.version) return false;
    const next = [notice, ...current.filter((n) => n.key !== notice.key)]
      .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
      .slice(0, MAX_NOTICES);
    await this.db.put(this.key, ITEM, next);
    this.cache = next;
    return true;
  }
}
